import { Alert, Platform, Vibration } from 'react-native';
import Constants from 'expo-constants';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebaseConfig';
import nativeFirestore from '@react-native-firebase/firestore';

/**
 * Escribe (o borra) el token de push del usuario con el SDK que corresponde.
 *
 * En el APK el SDK web va sin sesión y la regla `isSelf(uid)` deniega la
 * escritura, así que el token no se guardaba NUNCA y el backend no tenía a
 * dónde mandar los avisos. Es la causa de que la app no sonara jamás.
 *
 * Se deja que el error SUBA en vez de tragarlo: quien llama decide qué hacer,
 * y así un fallo aquí no vuelve a pasar desapercibido.
 */
async function guardarTokenDePush(
  uid: string,
  token: string | null,
  tokenFcm?: string | null
): Promise<void> {
  // `fcmToken` es el token NATIVO de FCM: el backend lo prefiere porque
  // entrega por admin.messaging() sin pasar por el servicio de Expo (que hoy
  // no tiene la credencial FCM y rechaza todo con InvalidCredentials).
  const alBorrar = { pushToken: null, fcmToken: null };

  if (Platform.OS !== 'web') {
    await nativeFirestore()
      .collection('users')
      .doc(uid)
      .set(
        token
          ? {
              pushToken: token,
              ...(tokenFcm ? { fcmToken: tokenFcm } : {}),
              pushTokenUpdatedAt: nativeFirestore.FieldValue.serverTimestamp(),
            }
          : alBorrar,
        { merge: true }
      );
    return;
  }
  await setDoc(
    doc(db, 'users', uid),
    token
      ? {
          pushToken: token,
          ...(tokenFcm ? { fcmToken: tokenFcm } : {}),
          pushTokenUpdatedAt: serverTimestamp(),
        }
      : alBorrar,
    { merge: true }
  );
}

// expo-notifications se importa de forma defensiva: si el paquete aún no está
// instalado (npm install expo-notifications), el servicio cae al Alert legacy
// para no romper la app en desarrollo.
let Notifications: any = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  Notifications = require('expo-notifications');
} catch {
  Notifications = null;
}

if (Notifications && Platform.OS !== 'web') {
  // Configurar handler global: notificaciones recibidas en foreground
  // se muestran como banner + sonido (comportamiento esperado por Uber/inDriver).
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldPlaySound: true,
      shouldSetBadge: false,
      shouldShowBanner: true,
      shouldShowList: true,
    }),
  });
}

class NotificationService {
  /**
   * CANALES DE ANDROID — SE CREAN ANTES QUE NADA. NO MOVER DESPUÉS DEL TOKEN.
   *
   * Por qué el orden importa: en Android 8+ una notificación dirigida a un
   * canal que todavía no existe NO SE MUESTRA (ni suena, ni vibra, ni aparece),
   * y un canal no se puede modificar una vez creado. Antes esta creación vivía
   * al FINAL del mismo `try` que pide el permiso y el token: bastaba que
   * `getExpoPushTokenAsync` fallara en el primer arranque —sin datos, Play
   * Services caídos, Expo sin responder— para que el `catch` se saltara la
   * creación y el teléfono quedara MUDO DE FORMA PERMANENTE, sin ningún error
   * visible. Eso explica que el mismo APK suene en un teléfono y en otro no.
   *
   * Va también antes de pedir el permiso: crear un canal no exige permiso, y
   * así el canal existe aunque el usuario diga "no" hoy y acepte mañana desde
   * los ajustes del sistema.
   *
   * Cada canal en su propio try: el fallo de uno no puede dejar sin crear a los
   * demás.
   */
  private async asegurarCanalesAndroid(): Promise<void> {
    if (!Notifications || Platform.OS !== 'android') return;

    try {
      await Notifications.setNotificationChannelAsync('default', {
        name: 'Go Safely',
        importance: Notifications.AndroidImportance?.HIGH ?? 4,
        vibrationPattern: [0, 250, 250, 250],
        lightColor: '#208AEF',
      });
    } catch (e) {
      console.warn('[notificationService] crear canal default falló:', e);
    }

    try {
      // Canal APARTE para pedidos nuevos de restaurante. Va en MAX (no HIGH):
      // en Android eso es lo que produce el aviso emergente sobre cualquier
      // pantalla, con sonido, aunque el teléfono esté en reposo. Un pedido no
      // atendido es dinero perdido para el restaurante, así que este aviso
      // tiene que comportarse como una alarma, no como un mensaje más.
      // Al ser un canal propio, el dueño puede ajustarle el volumen sin
      // silenciar el resto de la app (y sin poder silenciarlo por descuido
      // junto con las notificaciones normales).
      await Notifications.setNotificationChannelAsync('orders', {
        name: 'Pedidos nuevos',
        description: 'Aviso sonoro cuando entra un pedido a tu restaurante.',
        importance: Notifications.AndroidImportance?.MAX ?? 5,
        vibrationPattern: [0, 400, 200, 400, 200, 400],
        lightColor: '#22C55E',
        enableVibrate: true,
        showBadge: true,
      });
    } catch (e) {
      console.warn('[notificationService] crear canal orders falló:', e);
    }

    // El canal de la notificación persistente del viaje se crea aquí también,
    // y no solo la primera vez que se postea: si se creara tarde, el primer
    // aviso de viaje del día se perdería por la misma razón que los otros.
    await this.ensureOngoingChannel();
  }

  /**
   * Pide permisos, obtiene el Expo Push Token y lo persiste en
   * /users/{uid}.pushToken para que Cloud Functions pueda enviar push remotos.
   */
  async registerForPushNotifications(uid: string): Promise<string | null> {
    if (!Notifications || Platform.OS === 'web') return null;

    // PRIMERO los canales, y FUERA del try del token (ver el comentario de
    // asegurarCanalesAndroid): si el registro del token falla, los canales ya
    // están creados y el teléfono sigue pudiendo sonar.
    await this.asegurarCanalesAndroid();

    if (!Constants?.isDevice && Platform.OS !== 'ios') {
      // En emulador Android no funciona; en simulador iOS sí.
      // No bloqueamos, solo devolvemos null.
    }

    try {
      const { status: existing } = await Notifications.getPermissionsAsync();
      let finalStatus = existing;
      if (existing !== 'granted') {
        const { status } = await Notifications.requestPermissionsAsync();
        finalStatus = status;
      }
      if (finalStatus !== 'granted') {
        console.log('Push notifications permission denied');
        return null;
      }

      const projectId =
        Constants?.expoConfig?.extra?.eas?.projectId ??
        Constants?.easConfig?.projectId;
      const tokenResp = projectId
        ? await Notifications.getExpoPushTokenAsync({ projectId })
        : await Notifications.getExpoPushTokenAsync();
      const token = tokenResp?.data ?? null;

      // ── TOKEN NATIVO DE FCM, ADEMÁS DEL DE EXPO ─────────────────────────
      //
      // En la prueba real del 14-ago los logs del servidor mostraron que TODOS
      // los envíos morían en el mismo punto:
      //
      //   "Unable to retrieve the FCM server key ... InvalidCredentials"
      //
      // El token de Expo se guardaba bien (ese fallo ya se arregló), pero el
      // servicio de push de Expo necesita que la credencial FCM del proyecto
      // esté subida a SU consola para poder entregar en Android — y no lo está.
      //
      // Este token es el de FCM directo: con él, el backend entrega por
      // `admin.messaging()`, que usa la credencial propia del proyecto Firebase
      // y no depende de Expo ni de configurar ninguna consola externa.
      // Se guardan los dos y el backend prefiere el nativo.
      let tokenFcm: string | null = null;
      if (Platform.OS === 'android') {
        try {
          const nativo = await Notifications.getDevicePushTokenAsync();
          tokenFcm = typeof nativo?.data === 'string' ? nativo.data : null;
        } catch (e: any) {
          console.warn('[notificationService] token FCM nativo falló:', e?.message);
        }
      }

      if (token && uid) {
        // ⚠️ SDK NATIVO. AQUÍ ESTABA EL FALLO QUE DEJABA LA APP MUDA.
        //
        // Esto usaba `setDoc(doc(db, ...))` — el SDK WEB de Firestore. En el
        // APK ese SDK va SIN SESIÓN (el login real vive en
        // @react-native-firebase, ver firebaseConfig.ts), así que la regla
        // `isSelf(uid)` denegaba la escritura y el token NUNCA se guardaba.
        //
        // Consecuencia: `users/{uid}.pushToken` no existía, `sendPushToUid`
        // salía por "sin token válido para uid=" en TODOS los envíos, y NINGÚN
        // aviso del backend llegaba jamás al teléfono: ni "tu chofer llegó",
        // ni "viaje en curso", ni nada. La máquina de estados estaba bien; lo
        // que faltaba era el buzón.
        //
        // Este mismo error ya se había corregido cuatro veces en
        // firestoreService.ts; este servicio fue el único que quedó sin la
        // rama nativa.
        await guardarTokenDePush(uid, token, tokenFcm);
      }

      return token;
    } catch (e) {
      console.warn('Error registrando push notifications:', e);
      return null;
    }
  }

  /**
   * Dispara una notificación local. En foreground muestra banner real;
   * fuera de app suena/vibra normalmente.
   */
  async sendPushNotification(title: string, body: string, data?: any) {
    if (Notifications && Platform.OS !== 'web') {
      try {
        await Notifications.scheduleNotificationAsync({
          content: {
            title,
            body,
            data: data ?? {},
            sound: 'default',
          },
          trigger: null, // inmediata
        });
        return;
      } catch (e) {
        console.warn('Fallback a Alert por error en scheduleNotificationAsync:', e);
      }
    }

    // Fallback (web o sin expo-notifications): mostramos un Alert in-app
    // para no perder la señal al usuario.
    Alert.alert(`🔔 ${title}`, body, [{ text: 'Entendido' }]);
  }

  /**
   * ALARMA DE PEDIDO NUEVO (restaurante).
   *
   * No es una notificación más: es la señal de la que depende que el pedido se
   * atienda. Por eso combina dos canales independientes, para que el fallo de
   * uno no deje al restaurante sordo:
   *   1. VIBRACIÓN — local, no necesita internet ni permisos de notificación.
   *      Es lo único garantizado si el teléfono está sin datos o el permiso de
   *      notificaciones fue denegado.
   *   2. Notificación en el canal 'orders' (importancia MAX): sonido fuerte y
   *      aviso emergente por encima de lo que esté en pantalla.
   *
   * A propósito NO se usa un sonido descargado de internet como en el mapa del
   * chofer: si la conexión falla justo cuando entra el pedido, el sonido no
   * llega. Aquí se usa el del sistema, que es local.
   */
  async alertNewOrder(title: string, body: string, data?: any) {
    try {
      Vibration.vibrate([0, 400, 200, 400, 200, 400]);
    } catch { /* algunos dispositivos no vibran; seguimos igual */ }

    if (Notifications && Platform.OS !== 'web') {
      try {
        await Notifications.scheduleNotificationAsync({
          content: {
            title,
            body,
            data: data ?? {},
            sound: 'default',
            priority: Notifications.AndroidNotificationPriority?.MAX ?? 'max',
            vibrate: [0, 400, 200, 400, 200, 400],
          },
          // El canal solo se puede fijar en el disparador, así que se programa
          // a 1 segundo en vez de inmediata. Un segundo no cambia nada para el
          // restaurante y a cambio suena por el canal fuerte y no por el normal.
          trigger:
            Platform.OS === 'android'
              ? { seconds: 1, channelId: 'orders' }
              : null,
        });
        return;
      } catch (e) {
        console.warn('[alertNewOrder] no se pudo programar en el canal orders:', e);
        // Segundo intento por la vía normal: mejor sonar en el canal corriente
        // que quedarse callado.
        try {
          await this.sendPushNotification(title, body, data);
          return;
        } catch { /* cae al Alert de abajo */ }
      }
    }

    Alert.alert(`🔔 ${title}`, body, [{ text: 'Ver pedido' }]);
  }

  /**
   * Dispara una alerta general de Marketing cuando un restaurante activa una oferta.
   */
  async notifyRestaurantPromo(restaurantName: string, promoType: string) {
    let title = '¡Nueva Oferta Cerca de Ti!';
    let body = `El restaurante ${restaurantName} acaba de lanzar una oferta.`;

    if (promoType === 'DELIVERY') {
      title = '🛵 ¡Delivery GRATIS!';
      body = `Pide ahora en ${restaurantName} y no pagues envío.`;
    } else if (promoType === 'VIERNES_LOCO') {
      title = '🔥 ¡VIERNES LOCO ACTIVADO!';
      body = `¡Corre! ${restaurantName} acaba de activar 50% OFF en toda la tienda.`;
    }

    await this.sendPushNotification(title, body, { type: 'PROMO', restaurantName });
  }

  async notifyLoyaltyProgress(tripsCompleted: number) {
    if (tripsCompleted === 9) {
      await this.sendPushNotification(
        '🎁 ¡Tu próximo viaje es GRATIS!',
        'Has completado 9 viajes con Go Safely. ¡El número 10 corre por la casa!',
        { type: 'LOYALTY' }
      );
    } else if (tripsCompleted === 10) {
      await this.sendPushNotification(
        '🎉 ¡Viaje Completamente GRATIS!',
        '¡Gracias por tu lealtad! Disfruta este viaje sin costo.',
        { type: 'LOYALTY_REWARD' }
      );
    }
  }

  async notifyDriverIncentive(tripsCompletedToday: number) {
    if (tripsCompletedToday === 4) {
      await this.sendPushNotification(
        '🚀 ¡Estás a 1 viaje de tu meta diaria!',
        'Completa 5 viajes hoy y reduce aún más tu cuota mensual.',
        { type: 'DRIVER_INCENTIVE' }
      );
    }
  }

  // ───────────────────────────────────────────────────────────────────────
  // NOTIFICACIÓN PERSISTENTE TIPO UBER (ride status sticky en barra)
  // ───────────────────────────────────────────────────────────────────────
  // Por qué — Cuando el user minimiza la app durante "Buscando viaje" o
  // viaje activo, antes la app perdía visibilidad. Ahora una notificación
  // ongoing/sticky se queda en la barra del sistema mostrando el estado,
  // y al tocarla regresa a la app exactamente donde estaba. Mismo patrón
  // que Uber/inDrive/DiDi (no es una burbuja flotante chat-head).
  //
  // Implementación: expo-notifications con `sticky:true` + `ongoing:true`
  // en el canal `'ongoing-ride'`. Solo Android (en iOS las notificaciones
  // locales no se pueden hacer truly persistent — el sistema las maneja).

  private rideNotificationId: string | null = null;

  private async ensureOngoingChannel() {
    if (!Notifications || Platform.OS !== 'android') return;
    try {
      await Notifications.setNotificationChannelAsync('ongoing-ride', {
        name: 'Estado del viaje',
        description: 'Notificación que se mantiene visible mientras buscas viaje o estás en uno.',
        importance: Notifications.AndroidImportance?.HIGH ?? 4,
        vibrationPattern: [0, 0], // sin vibrar, es solo informativa
        lightColor: '#208AEF',
        showBadge: false,
        enableVibrate: false,
      });
    } catch (e) {
      console.warn('[notificationService] crear canal ongoing-ride falló:', e);
    }
  }

  /**
   * Posta o actualiza la notificación persistente del viaje.
   * Llamar al entrar en estado "Buscando" y cada vez que cambie el estado.
   *
   * @param state SEARCHING | DRIVER_ASSIGNED | IN_TRIP
   * @param subtitle Texto pequeño descriptivo (ej. "$200 DOP" o "Chofer en camino")
   * @param rideId ID del ride activo — CRÍTICO para deep-link. Sin esto,
   *   el tap de la notificación no puede restaurar la UI al ride correcto.
   */
  async postPersistentRideStatus(
    state: 'SEARCHING' | 'DRIVER_ASSIGNED' | 'ARRIVED' | 'IN_TRIP',
    subtitle?: string,
    rideId?: string | null
  ) {
    if (!Notifications || Platform.OS === 'web') return;
    try {
      await this.ensureOngoingChannel();

      const titleByState: Record<typeof state, string> = {
        SEARCHING: '🔍 Buscando viaje…',
        DRIVER_ASSIGNED: '🚗 Chofer en camino',
        // FIX NOTIF — Estado ARRIVED: el chofer llegó al punto de recogida.
        ARRIVED: '🚗 Tu chofer llegó',
        IN_TRIP: '🛣️ Viaje en curso',
      };
      const bodyByState: Record<typeof state, string> = {
        SEARCHING: subtitle ?? 'Toca para ver las ofertas de los choferes.',
        DRIVER_ASSIGNED: subtitle ?? 'Toca para ver detalles del chofer.',
        ARRIVED: subtitle ?? 'Tu chofer está en el punto esperándote. Toca para ver.',
        IN_TRIP: subtitle ?? 'Toca para volver al mapa.',
      };

      // Si ya hay una notificación previa, la actualizamos cancelándola
      // y posteando la nueva con el mismo identifier conceptual.
      if (this.rideNotificationId) {
        try {
          await Notifications.dismissNotificationAsync(this.rideNotificationId);
        } catch {}
      }

      const id = await Notifications.scheduleNotificationAsync({
        identifier: 'gs-ride-status',
        content: {
          title: titleByState[state],
          body: bodyByState[state],
          // Payload extendido — Uber-style. Al tocar la notificación después
          // de un force-close, el router lee `rideId` y restaura el listener
          // + la UI del viaje en curso EXACTO, no la pantalla de solicitud.
          data: {
            type: 'RIDE_STATUS',
            state,
            rideId: rideId ?? null,
            deepLinkPath: rideId ? `/(tabs)/map?rideId=${rideId}` : '/(tabs)/map',
          },
          sound: undefined, // sin sonido (es solo informativa)
          sticky: true,     // Android: no se puede deslizar para borrar
          ongoing: true,    // Android: marcador de tarea en curso
          autoDismiss: false,
          priority: Notifications.AndroidNotificationPriority?.HIGH ?? 'high',
          color: '#208AEF',
        },
        // channelId va en el trigger, no en content — mismo patrón que alertNewOrder.
        // Sin esto la persistente caía al canal 'default' y perdía sticky real.
        trigger:
          Platform.OS === 'android'
            ? ({ seconds: 1, channelId: 'ongoing-ride' } as any)
            : null,
      });
      this.rideNotificationId = id;
    } catch (e) {
      console.warn('[notificationService] postPersistentRideStatus falló:', e);
    }
  }

  /**
   * FIX A-6 — Borra el pushToken del usuario en Firestore al hacer logout.
   * Sin esto, en un teléfono compartido (usuario A cierra sesión, usuario B
   * entra) el token de A quedaba en su doc y sus notificaciones llegaban al
   * dispositivo de B. Al re-loguear, registerForPushNotifications vuelve a
   * escribir el token del usuario correcto.
   */
  async clearPushToken(uid: string) {
    if (!uid) return;
    try {
      // Mismo motivo que al guardarlo: con el SDK web esto tampoco se
      // ejecutaba, así que el token del usuario anterior se quedaba pegado en
      // Firestore y sus avisos podían llegar al teléfono de otra persona.
      await guardarTokenDePush(uid, null);
    } catch (e) {
      console.warn('[notificationService] clearPushToken falló:', e);
    }
  }

  /**
   * Limpia la notificación persistente. Llamar al cancelar la búsqueda,
   * terminar el viaje, o al hacer signOut.
   */
  async clearPersistentRideStatus() {
    if (!Notifications || Platform.OS === 'web') return;
    try {
      if (this.rideNotificationId) {
        await Notifications.dismissNotificationAsync(this.rideNotificationId);
        this.rideNotificationId = null;
      }
      // Defensive: también intentamos dismiss por identifier conocido por
      // si quedó zombi de una sesión previa.
      try { await Notifications.dismissNotificationAsync('gs-ride-status'); } catch {}
    } catch (e) {
      console.warn('[notificationService] clearPersistentRideStatus falló:', e);
    }
  }
}

export const notificationService = new NotificationService();
